import React, {
    useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Modal from 'antd/lib/modal';

import { CombinedState } from 'reducers';
import type { Job } from 'cvat-core-wrapper';
import { changeFrameAsync, deleteFrameAsync } from 'actions/annotation-actions';
import { sharedThumbnailLoader } from 'utils/frame-thumbnails';
import { isReferenceJob } from 'utils/reference-job';
import { visibleFrames } from 'utils/visible-frames';
import { t } from 'utils/i18n';
import { openAddImagesModal } from './add-images-modal';
import { useSaveFrameOrder } from './save-frame-order';
import FrameStrip from './frame-strip';
import { useStepsContext } from './steps/steps-context';
import { frameStatusMap } from './steps/steps-model';

// a deleted frame is only hidden: Restore frame in the ⋯ menu brings it back
export function confirmDeleteFrame(noun: string, position: number, onOk: () => void): void {
    Modal.confirm({
        title: t(`Delete ${noun} {n}?`, { n: position }),
        content: t(`The ${noun} is hidden and skipped by export. Open it with "Show deleted frames" to restore it.`),
        okText: t('Delete'),
        okButtonProps: { danger: true },
        onOk,
    });
}

export default function FrameStripContainer(): JSX.Element | null {
    const dispatch = useDispatch();
    const job = useSelector((state: CombinedState) => state.annotation.job.instance);
    const current = useSelector((state: CombinedState) => state.annotation.player.frame.number);
    // Flips when the current frame is deleted or restored: the moment the list must refresh.
    const deletedVersion = useSelector((state: CombinedState) => state.annotation.player.frame.data?.deleted);
    const [numbers, setNumbers] = useState<number[]>([]);
    const [urls, setUrls] = useState<Record<number, string>>({});
    const [names, setNames] = useState<Record<number, string>>({});
    const requested = useRef(new Set<number>());
    const saveFrameOrder = useSaveFrameOrder();
    const steps = useStepsContext();
    const stepsState = steps?.state ?? null;
    const status = useMemo(() => frameStatusMap(stepsState), [stepsState]);
    const mounted = useRef(true);
    useEffect(() => () => { mounted.current = false; }, []);

    const loader = useMemo(() => (job ? sharedThumbnailLoader('jobs', job.id) : null), [job?.id]);
    useEffect(() => {
        requested.current = new Set();
        setUrls({});
    }, [loader]);

    useEffect(() => {
        let cancelled = false;
        if (job) visibleFrames(job as any).then((list) => { if (!cancelled) setNumbers(list); });
        return () => { cancelled = true; };
    }, [job?.id, deletedVersion]);

    useEffect(() => {
        let cancelled = false;
        if (job) {
            Promise.all(numbers.map((n) => job.frames.get(n).then((data) => [n, data.filename] as const)))
                .then((pairs) => { if (!cancelled) setNames(Object.fromEntries(pairs)); })
                // a name is only a caption: without it the tile just stays unlabelled
                .catch(() => {});
        }
        return () => { cancelled = true; };
    }, [job?.id, numbers]);

    useEffect(() => {
        const item = document.querySelector(`.cvat-workspace-frame-strip [data-frame="${current}"]`);
        if (item && typeof item.scrollIntoView === 'function') {
            item.scrollIntoView({ inline: 'nearest', block: 'nearest' });
        }
    }, [current, numbers]);

    const onVisible = useCallback((frame: number) => {
        if (!loader || requested.current.has(frame)) return;
        requested.current.add(frame);
        loader.load(frame)
            .then((url) => { if (mounted.current) setUrls((prev) => ({ ...prev, [frame]: url })); })
            .catch(() => requested.current.delete(frame));
    }, [loader]);

    if (!job || isReferenceJob(job) || numbers.length === 0) return null;

    return (
        <FrameStrip
            frames={numbers.map((n) => ({ number: n, url: urls[n] ?? null, name: names[n] }))}
            current={current}
            onSelect={(frame) => dispatch(changeFrameAsync(frame) as any)}
            onVisible={onVisible}
            status={status}
            selected={steps?.selected}
            onToggleSelect={steps?.toggleSelected}
            onAdd={() => openAddImagesModal(`/tasks/${(job as Job).taskId}/data`, 'drawings')}
            onReorder={(order) => saveFrameOrder(`/tasks/${(job as Job).taskId}/data`, order)}
            onDelete={(frame) => confirmDeleteFrame('drawing', numbers.indexOf(frame) + 1, () => {
                dispatch(deleteFrameAsync(frame) as any);
            })}
        />
    );
}
